/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { CSSProperties, FunctionComponent, JSX } from "react";
import { useEffect, useRef, useState } from "react";

import { ImageLoader } from "../ImageLoader";
import { OSD_MOVEMENT_TIME, OSD_STILL_TIME } from "../../../config";
import type { OsdProps } from "./OsdProps";

import "./Osd.scss";

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- Required by CodingGuidelines §7 (D-09)
const TAG: string = "Osd";

/**
 * Osd — branded message overlay shown above full-screen video playback.
 *
 * Presentational only: visibility is controlled by `OsdContainer`, which
 * renders this component solely while the OSD Redux state is visible. This
 * component owns NO dismiss-key logic of its own — it is a pure reflection
 * of whatever `OsdContainer` passes down; hiding is 100% caller-driven via
 * the `osd` Redux slice.
 *
 * Periodically repositions itself at random within its container after a
 * period of inactivity to avoid SmartTV screen burn-in, when
 * `enableAntiBurnReposition` is `true`. When `false`, the OSD is rendered as
 * an anchored (non-repositioning), pinned-in-place message instead — see
 * `osd-container--anchored` in `Osd.scss`.
 */
const Osd: FunctionComponent<OsdProps> = (props: OsdProps): JSX.Element => {
  const { message, code, brandingLogoUrl, enableAntiBurnReposition } = props;

  const shouldReposition: boolean = enableAntiBurnReposition;

  const osdContainerRef = useRef<HTMLDivElement | null>(null);
  const osdRef = useRef<HTMLDivElement | null>(null);
  const movementIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null
  );

  const [osdMovement, setOsdMovement] = useState<CSSProperties>({
    top: "auto",
    left: "auto"
  });

  useEffect((): (() => void) => {
    if (shouldReposition === false) {
      // No reset needed here: `OsdContainer` remounts this component (via
      // `key={enableAntiBurnReposition}`) whenever the reposition mode
      // changes, so `osdMovement` state is already fresh at its
      // "auto"/"auto" initial value.
      return (): void => {};
    }

    const updateOsdPosition = (): void => {
      const container = osdContainerRef.current;
      const osd = osdRef.current;
      if (container === null || osd === null) {
        return;
      }
      const containerHeight: number = container.offsetHeight;
      const containerWidth: number = container.offsetWidth;
      const osdWidth: number = osd.offsetWidth;
      const osdHeight: number = osd.offsetHeight;
      // eslint-disable-next-line sonarjs/pseudo-random -- non-cryptographic use, purely visual burn-in-prevention jitter
      const topMovement: number = Math.random() * (containerHeight - osdHeight);
      // eslint-disable-next-line sonarjs/pseudo-random -- non-cryptographic use, purely visual burn-in-prevention jitter
      const leftMovement: number = Math.random() * (containerWidth - osdWidth);
      setOsdMovement({ top: `${topMovement}px`, left: `${leftMovement}px` });
    };

    const stillTimer: ReturnType<typeof setTimeout> = setTimeout((): void => {
      movementIntervalRef.current = setInterval(
        updateOsdPosition,
        OSD_MOVEMENT_TIME
      );
    }, OSD_STILL_TIME);

    return (): void => {
      clearTimeout(stillTimer);
      if (movementIntervalRef.current !== null) {
        clearInterval(movementIntervalRef.current);
        movementIntervalRef.current = null;
      }
    };
  }, [shouldReposition]);

  const hasCode: boolean = code !== undefined && code !== "";
  const hasBrandingLogo: boolean =
    brandingLogoUrl !== undefined && brandingLogoUrl !== "";

  return (
    <div
      className={`osd-container${shouldReposition === false ? " osd-container--anchored" : ""}`}
      ref={osdContainerRef}
      data-testid="osd-container"
    >
      <div
        className="osd"
        style={shouldReposition ? osdMovement : undefined}
        ref={osdRef}
      >
        {hasBrandingLogo ? (
          <ImageLoader
            src={brandingLogoUrl}
            fallbackText="Partner branding logo"
            classStyle="osd__branding-icon"
            dataTestId="osd-branding-icon"
          />
        ) : null}
        <div
          className="osd__message"
          data-testid="osd-message"
          role="alert"
          aria-live="assertive"
        >
          {message}
        </div>
        {hasCode ? (
          <div className="osd__message-code" data-testid="osd-message-code">
            {code}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default Osd;
